import type { CleaningRequestSummary } from "@cleany/platform-contracts";
import { useCallback, useEffect, useState } from "react";
import { request } from "./api.js";

const services: Record<string, string> = {
  maintenance: "Поддерживающая уборка",
  general: "Генеральная уборка",
  post_renovation: "После ремонта",
};

const labels: Record<string, string> = {
  open: "Открыта",
  contact_shared: "Контакты переданы",
  closed: "Закрыта",
  cancelled: "Отменена",
  hidden: "Скрыта",
  expired: "Истекла",
};

export function MarketplaceCenter({ token }: { readonly token: string }) {
  const [rows, setRows] = useState<CleaningRequestSummary[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [offset, setOffset] = useState(0);
  const [reason, setReason] = useState("");
  const [selected, setSelected] = useState("");
  const [hasMore, setHasMore] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const page = await request<{ requests: CleaningRequestSummary[] }>(
        `/api/v1/operations/requests?limit=50&offset=${offset}`,
        {},
        token,
      );
      setRows(page.requests);
      setHasMore(page.requests.length === 50);
      setError("");
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Не удалось загрузить заявки");
    } finally {
      setBusy(false);
    }
  }, [token, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  async function hide() {
    if (!selected || reason.trim().length < 8) return;
    setBusy(true);
    try {
      await request(
        `/api/v1/operations/requests/${selected}/hide`,
        { method: "POST", body: JSON.stringify({ reason: reason.trim() }) },
        token,
      );
      setSelected("");
      setReason("");
      await load();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Модерация не выполнена");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="peopleCenter" aria-labelledby="marketplace-title">
      <div className="workspaceHeader adminSectionHeader">
        <div>
          <p className="eyebrow">Cleany · прямые предложения</p>
          <h1 id="marketplace-title">Заявки и предложения</h1>
          <p>
            Клиенты выбирают клинеров и договариваются напрямую. Управляйте публикациями и доступом
            пользователей.
          </p>
        </div>
        <button type="button" onClick={() => void load()} disabled={busy}>
          Обновить
        </button>
      </div>
      <div className="directoryMetrics">
        <div>
          <span>Заявок на странице</span>
          <strong>{rows.length}</strong>
        </div>
        <div>
          <span>Открытых</span>
          <strong>{rows.filter((row) => row.status === "open").length}</strong>
        </div>
        <div>
          <span>Предложений</span>
          <strong>{rows.reduce((sum, row) => sum + row.offerCount, 0)}</strong>
        </div>
      </div>
      {error && <p role="alert">{error}</p>}
      {busy && <p role="status">Загружаем…</p>}
      {!busy && !error && rows.length === 0 && <p>На этой странице заявок пока нет.</p>}
      <div className="marketplaceTable">
        <table>
          <thead>
            <tr>
              <th>Район и площадь</th>
              <th>Дата</th>
              <th>Ориентир</th>
              <th>Предложения</th>
              <th>Статус</th>
              <th>Действие</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  {row.city} · {row.district}
                  <br />
                  {row.home.areaM2} м² · {services[row.serviceCode] ?? "Уборка"}
                  <p>{row.notes}</p>
                </td>
                <td>{new Date(row.preferredAt).toLocaleString("ru-RU")}</td>
                <td>
                  {money(row.estimate.lowerMinor)} – {money(row.estimate.upperMinor)}
                </td>
                <td>
                  {row.offerCount}
                  <details>
                    <summary>Посмотреть</summary>
                    {row.offers.map((offer) => (
                      <p key={offer.id}>
                        {offer.displayName} · {money(offer.amountMinor)}
                        <br />
                        {offer.message}
                      </p>
                    ))}
                  </details>
                </td>
                <td>
                  {labels[row.status]}
                  {row.moderationReason && <p>{row.moderationReason}</p>}
                </td>
                <td>
                  <button
                    type="button"
                    disabled={busy || row.status === "hidden"}
                    onClick={() => {
                      setSelected(row.id);
                      setReason("");
                    }}
                  >
                    Скрыть
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selected && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void hide();
          }}
          className="marketplaceModeration"
        >
          <label htmlFor="moderation-reason">Причина скрытия</label>
          <input
            id="moderation-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            minLength={8}
            maxLength={300}
            required
          />
          <button type="submit" disabled={busy || reason.trim().length < 8}>
            Скрыть публикацию
          </button>
          <button type="button" onClick={() => setSelected("")}>
            Отмена
          </button>
        </form>
      )}
      <div className="marketplacePagination">
        <button
          type="button"
          disabled={busy || offset === 0}
          onClick={() => setOffset(Math.max(0, offset - 50))}
        >
          Назад
        </button>
        <span>Страница {offset / 50 + 1}</span>
        <button type="button" disabled={busy || !hasMore} onClick={() => setOffset(offset + 50)}>
          Далее
        </button>
      </div>
    </section>
  );
}

function money(minor: number) {
  return `${(minor / 100).toLocaleString("ru-RU")} ₸`;
}
